import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCoins, FaCrown, FaGift, FaHeart, FaRegUser, FaStar } from 'react-icons/fa';
import PointCircle from './PointCircle';
import RewardMilestoneCard from './RewardMilestoneCard';
import PointDetailModal from './PointDetailModal';
import { activityRules } from './cardData';
import cardApi from './cardApi';
import './CardLandingPage.css';

function CardLandingPage() {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [card, setCard] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [switchingMerchant, setSwitchingMerchant] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content');
    document.title = '無限貸｜會員尊榮集點卡';
    meta?.setAttribute('content', '無限貸會員尊榮集點活動，完成申請累積點數，解鎖專屬回饋。');

    return () => {
      document.title = previousTitle;
      if (previousDescription) meta?.setAttribute('content', previousDescription);
    };
  }, []);

  useEffect(() => {
    if (!cardApi.getCustomerToken()) { navigate('/login', { replace: true }); return; }
    Promise.all([cardApi.getCard(), cardApi.getMerchants()]).then(([activeCard, merchantRows]) => { setCard(activeCard); setMerchants(merchantRows); }).catch(() => { cardApi.logoutCustomer(); navigate('/login', { replace: true }); });
  }, [navigate]);

  if (!card) return <div className="card-loading">載入您的集點卡…</div>;

  const currentPoints = card.membership.currentPoints;
  const pendingPoint = card.claims.find((claim) => claim.status === 'PENDING')?.point_number || null;
  const merchantName = card.merchant.name;
  const visibleMerchants = card.merchant.active === false ? [...merchants, card.merchant] : merchants;

  const nextReward = card.rewards.find((reward) => reward.milestone > currentPoints) || card.rewards[card.rewards.length - 1];

  const getPointState = (point) => {
    if (point <= currentPoints) return 'claimed';
    if (point === pendingPoint) return 'pending';
    if (point === currentPoints + 1 && !card.membership.claimLocked) return 'available';
    return 'locked';
  };

  const handleSelectPoint = (number, state) => {
    setSelectedPoint({ number, state });
  };

  const handleConfirm = async () => {
    try { setCard(await cardApi.claim()); setSelectedPoint(null); }
    catch (err) { setError(err.message); setSelectedPoint(null); }
  };

  const handleMerchantChange = async (event) => {
    setError('');
    setSwitchingMerchant(true);
    try { setCard(await cardApi.selectMerchant(Number(event.target.value))); }
    catch (err) { setError(err.message); }
    finally { setSwitchingMerchant(false); }
  };

  return (
    <div className="loyalty-card-page">
      <div className="card-ambient card-ambient--one" aria-hidden="true" />
      <div className="card-ambient card-ambient--two" aria-hidden="true" />

      <header className="card-hero">
        <div className="card-hero__ribbon" aria-hidden="true" />
        <div className="card-hero__sparkles" aria-hidden="true">
          <FaStar /><FaStar /><FaStar /><FaStar />
        </div>
        <div className="card-brand-mark">
          <FaCrown aria-hidden="true" />
          <span>{merchantName}</span>
        </div>
        <p className="card-hero__eyebrow">MEMBER REWARDS</p>
        <h1><span>尊榮會員</span>集點卡</h1>
        <p className="card-hero__subtitle">每一次的信任，都是我們前進的動力</p>
        <div className="card-hero__promise">
          <span>點數累積</span><i />
          <span>好禮不斷</span><i />
          <span>安心回饋</span>
        </div>
        <FaCoins className="card-hero__coins" aria-hidden="true" />
        <FaGift className="card-hero__gift" aria-hidden="true" />
      </header>

      <main className="card-main">
        <section className="member-panel" aria-label="會員集點進度">
          <div className="member-panel__profile">
            <div className="member-panel__avatar"><FaRegUser aria-hidden="true" /></div>
            <div>
              <p>歡迎回來</p>
              <h2>{card.customer.name}</h2>
              <span>{card.customer.maskedId}</span>
            </div>
          </div>
          <label className="member-panel__merchant-select">簽到商戶
            <select value={card.merchant.id} onChange={handleMerchantChange} disabled={switchingMerchant}>
              {visibleMerchants.map((merchant) => <option key={merchant.id} value={merchant.id}>{merchant.name}{merchant.active === false ? '（已隱藏）' : ''}</option>)}
            </select>
          </label>
          <div className="member-panel__summary">
            <div className="member-panel__points">
              <span>目前點數</span>
              <strong>{currentPoints}<small> / 30 點</small></strong>
            </div>
            <div className="member-panel__next">
              再 <strong>{Math.max(0, nextReward.milestone - currentPoints)}</strong> 點可獲得
              <b>{nextReward.value}{nextReward.unit}回饋</b>
            </div>
          </div>
          <div className="member-panel__progress" aria-label={`目前 ${currentPoints} 點，共 30 點`}>
            <span style={{ width: `${(currentPoints / 30) * 100}%` }} />
          </div>
        </section>

        <section className="stamp-card" aria-labelledby="stamp-card-title">
          <div className="stamp-card__heading">
            <div>
              <p>COLLECT & REWARD</p>
              <h2 id="stamp-card-title">我的集點卡</h2>
            </div>
            <span className="stamp-card__badge"><FaHeart /> 集一份信任</span>
          </div>

          <div className="stamp-card__rows">
            {card.rewards.map((reward, rowIndex) => {
              const firstPoint = rowIndex * 5 + 1;
              const points = Array.from({ length: 5 }, (_, index) => firstPoint + index);
              return (
                <div className="stamp-row" key={reward.milestone}>
                  <div className="stamp-row__points">
                    {points.map((point) => (
                      <PointCircle
                        key={point}
                        number={point}
                        state={getPointState(point)}
                        claimedAt={card.claims.find((claim) => claim.point_number === point)?.approved_at || card.claims.find((claim) => claim.point_number === point)?.requested_at}
                        onSelect={handleSelectPoint}
                      />
                    ))}
                  </div>
                  <RewardMilestoneCard reward={reward} currentPoints={currentPoints} />
                </div>
              );
            })}
          </div>

          <p className="stamp-card__footer"><FaHeart /> 集一份信任・換一份回饋 <FaHeart /></p>
        </section>

        <section className="activity-panel" aria-labelledby="activity-title">
          <div className="activity-panel__heading">
            <span><FaGift aria-hidden="true" /></span>
            <div><p>EVENT INFORMATION</p><h2 id="activity-title">活動說明</h2></div>
          </div>
          <ol className="activity-panel__rules">
            {activityRules.map((rule, index) => (
              <li key={rule}><span>{index + 1}</span><p>{rule}</p></li>
            ))}
          </ol>
          <div className="activity-panel__thanks">
            <FaHeart /> 感謝有您，一路相挺
          </div>
        </section>
      </main>

      <footer className="card-page-footer">
        <p>信任・專業・與您同行</p>
        <span>活動詳情請洽您的服務專員</span>
      </footer>

      {error && <div className="card-page-error">{error}</div>}
      <PointDetailModal
        selected={selectedPoint}
        merchantName={merchantName}
        onClose={() => setSelectedPoint(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

export default CardLandingPage;
